import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { format } from 'date-fns';
import { CalendarIcon, Upload, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);

    // Form data
    const [clientInfo, setClientInfo] = useState({
        email: '',
        full_name: '',
        mobile_number: '',
        complete_address: ''
    });

    const [petInfo, setPetInfo] = useState({
        name: '',
        species: '',
        breed: '',
        age: '',
        weight: '',
        weight_unit: 'kg',
        birth_year: '',
        gender: '',
        spayed_neutered: ''
    });

    const [consultationInfo, setConsultationInfo] = useState({
        consultation_type: 'general',
        main_concern: '',
        symptom_duration: '',
        appetite: '',
        prescription_needed: false,
        preferred_schedule_date: null,
        preferred_schedule_time: '',
        preferred_platform: ''
    });

    const [consentForm, setConsentForm] = useState({
        limitation_acknowledgment: false,
        liability_agreement: false,
        emergency_understanding: false,
        information_accuracy: false
    });

    // File upload
    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg'],
            'video/*': ['.mp4', '.mov']
        },
        maxSize: 10485760,
        onDrop: async (acceptedFiles) => {
            setLoading(true);
            try {
                const uploadPromises = acceptedFiles.map(async (file) => {
                    const formData = new FormData();
                    formData.append('file', file);
                    const response = await axios.post(`${API}/upload`, formData);
                    return response.data.file_id;
                });
                const fileIds = await Promise.all(uploadPromises);
                setUploadedFiles([...uploadedFiles, ...fileIds]);
                toast.success('Files uploaded successfully');
            } catch (error) {
                toast.error('Failed to upload files');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleDateSelect = async (date) => {
        setConsultationInfo({ ...consultationInfo, preferred_schedule_date: date, preferred_schedule_time: '' });
        try {
            const response = await axios.get(`${API}/available-slots`, {
                params: { 
                    date: format(date, 'yyyy-MM-dd'),
                    consultation_type: consultationInfo.consultation_type
                }
            });
            setAvailableSlots(response.data.available_slots);
        } catch (error) {
            toast.error('Failed to fetch available slots');
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const bookingData = {
                client: clientInfo,
                pet: {
                    ...petInfo,
                    age: parseInt(petInfo.age)
                },
                consultation: {
                    ...consultationInfo,
                    preferred_schedule_date: format(consultationInfo.preferred_schedule_date, 'yyyy-MM-dd'),
                    media_files: uploadedFiles
                },
                consent: consentForm
            };

            const response = await axios.post(`${API}/bookings`, bookingData);
            const booking = response.data;

            navigate('/payment', { 
                state: { 
                    bookingId: booking.id,
                    bookingReference: booking.booking_reference,
                    amount: booking.consultation_fee,
                    clientEmail: clientInfo.email,
                    consultationType: consultationInfo.consultation_type
                } 
            });
        } catch (error) {
            toast.error('Failed to create booking. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const validateStep = () => {
        if (step === 1) {
            return clientInfo.email && clientInfo.full_name && clientInfo.mobile_number && clientInfo.complete_address;
        }
        if (step === 2) {
            return petInfo.name && petInfo.species && petInfo.breed && petInfo.age && petInfo.weight && petInfo.gender && petInfo.spayed_neutered;
        }
        if (step === 3) {
            return consultationInfo.main_concern && consultationInfo.symptom_duration && consultationInfo.appetite;
        }
        if (step === 4) {
            return consultationInfo.preferred_schedule_date && consultationInfo.preferred_schedule_time && consultationInfo.preferred_platform;
        }
        if (step === 5) {
            return consentForm.limitation_acknowledgment && consentForm.liability_agreement && 
                   consentForm.emergency_understanding && consentForm.information_accuracy;
        }
        return true;
    };

    const steps = [
        { number: 1, title: 'Client Info' },
        { number: 2, title: 'Pet Info' },
        { number: 3, title: 'Consultation' },
        { number: 4, title: 'Schedule' },
        { number: 5, title: 'Consent' },
        { number: 6, title: 'Review' }
    ];

    const consultationFee = consultationInfo.consultation_type === 'general' ? 499 : 799;

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#fcf6e3' }}>
            <header className="glass-header py-4">
                <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
                    <div>
                        <h1 
                            className="text-xl sm:text-2xl font-bold cursor-pointer" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            onClick={() => navigate('/')}
                        >
                            MVC VET TELECARE
                        </h1>
                        <p className="text-sm" style={{ color: '#d9a520' }}>Licensed Veterinarians</p>
                    </div>
                    <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-home-btn">
                        <ArrowLeft className="mr-2" /> Back to Home
                    </Button>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-12">
                {/* Step Indicator */}
                <div className="mb-12 overflow-x-auto">
                    <div className="flex justify-between items-center min-w-max">
                        {steps.map((s, idx) => (
                            <div key={s.number} className="flex items-center" data-testid={`step-indicator-${s.number}`}>
                                <div 
                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                        step >= s.number ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                                    }`}
                                >
                                    {step > s.number ? <Check className="w-5 h-5" /> : s.number}
                                </div>
                                <span className="ml-2 text-sm whitespace-nowrap">{s.title}</span>
                                {idx < steps.length - 1 && (
                                    <div className={`w-8 h-1 mx-2 ${step > s.number ? 'bg-primary' : 'bg-muted'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <motion.div className="card p-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <AnimatePresence mode="wait">
                        {/* Step 1: Client Information */}
                        {step === 1 && (
                            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Client Information
                                </h2>
                                <div>
                                    <Label htmlFor="full-name">Full Name *</Label>
                                    <Input id="full-name" data-testid="full-name-input" value={clientInfo.full_name} onChange={(e) => setClientInfo({ ...clientInfo, full_name: e.target.value })} placeholder="Juan Dela Cruz" />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email Address *</Label>
                                    <Input id="email" data-testid="email-input" type="email" value={clientInfo.email} onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })} placeholder="juan@example.com" />
                                </div>
                                <div>
                                    <Label htmlFor="mobile">Mobile Number *</Label>
                                    <Input id="mobile" data-testid="mobile-input" value={clientInfo.mobile_number} onChange={(e) => setClientInfo({ ...clientInfo, mobile_number: e.target.value })} placeholder="+63 912 345 6789" />
                                </div>
                                <div>
                                    <Label htmlFor="address">Complete Address *</Label>
                                    <Textarea id="address" data-testid="address-input" value={clientInfo.complete_address} onChange={(e) => setClientInfo({ ...clientInfo, complete_address: e.target.value })} placeholder="Complete address" rows={3} />
                                </div>
                            </motion.div>
                        )}

                        {/* Step 2: Pet Information */}
                        {step === 2 && (
                            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Pet Information
                                </h2>
                                <div>
                                    <Label htmlFor="pet-name">Pet&apos;s Name *</Label>
                                    <Input id="pet-name" data-testid="pet-name-input" value={petInfo.name} onChange={(e) => setPetInfo({ ...petInfo, name: e.target.value })} placeholder="Max" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="species">Species *</Label>
                                        <Select value={petInfo.species} onValueChange={(value) => setPetInfo({ ...petInfo, species: value })}>
                                            <SelectTrigger id="species" data-testid="species-select">
                                                <SelectValue placeholder="Select species" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="dog">Dog</SelectItem>
                                                <SelectItem value="cat">Cat</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="breed">Breed *</Label>
                                        <Input id="breed" data-testid="breed-input" value={petInfo.breed} onChange={(e) => setPetInfo({ ...petInfo, breed: e.target.value })} placeholder="Labrador" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="age">Age (years) *</Label>
                                        <Input id="age" data-testid="age-input" type="number" value={petInfo.age} onChange={(e) => setPetInfo({ ...petInfo, age: e.target.value })} placeholder="3" />
                                    </div>
                                    <div>
                                        <Label htmlFor="gender">Gender *</Label>
                                        <Select value={petInfo.gender} onValueChange={(value) => setPetInfo({ ...petInfo, gender: value })}>
                                            <SelectTrigger id="gender" data-testid="gender-select">
                                                <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="male">Male</SelectItem>
                                                <SelectItem value="female">Female</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="weight">Weight *</Label>
                                        <div className="flex gap-2">
                                            <Input id="weight" data-testid="weight-input" type="number" step="0.1" value={petInfo.weight} onChange={(e) => setPetInfo({ ...petInfo, weight: e.target.value })} placeholder="5.5" className="flex-1" />
                                            <Select value={petInfo.weight_unit} onValueChange={(value) => setPetInfo({ ...petInfo, weight_unit: value })}>
                                                <SelectTrigger className="w-20">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="kg">kg</SelectItem>
                                                    <SelectItem value="lbs">lbs</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="birth-year">Birth Year</Label>
                                        <Input id="birth-year" data-testid="birth-year-input" type="number" value={petInfo.birth_year} onChange={(e) => setPetInfo({ ...petInfo, birth_year: e.target.value })} placeholder="2021" min="2000" max={new Date().getFullYear()} />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="spayed">Spayed/Neutered? *</Label>
                                    <Select value={petInfo.spayed_neutered} onValueChange={(value) => setPetInfo({ ...petInfo, spayed_neutered: value })}>
                                        <SelectTrigger id="spayed" data-testid="spayed-select">
                                            <SelectValue placeholder="Select option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="yes">Yes</SelectItem>
                                            <SelectItem value="no">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 3: Consultation Details */}
                        {step === 3 && (
                            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Consultation Details
                                </h2>
                                <div>
                                    <Label>Type of Consultation *</Label>
                                    <RadioGroup value={consultationInfo.consultation_type} onValueChange={(value) => setConsultationInfo({ ...consultationInfo, consultation_type: value })}>
                                        <div className="flex items-center space-x-2 p-3 border rounded-lg">
                                            <RadioGroupItem value="general" id="general" data-testid="consultation-general" />
                                            <label htmlFor="general" className="flex-1 cursor-pointer">
                                                <p className="font-semibold">General Online Consultation</p>
                                                <p className="text-sm text-muted-foreground">9:00 AM - 7:00 PM | ₱499</p>
                                            </label>
                                        </div>
                                        <div className="flex items-center space-x-2 p-3 border rounded-lg">
                                            <RadioGroupItem value="emergency" id="emergency" data-testid="consultation-emergency" />
                                            <label htmlFor="emergency" className="flex-1 cursor-pointer">
                                                <p className="font-semibold">Emergency Consultation</p>
                                                <p className="text-sm text-muted-foreground">7:00 PM - 9:00 AM | ₱799</p>
                                            </label>
                                        </div>
                                    </RadioGroup>
                                </div>
                                <div>
                                    <Label htmlFor="main-concern">Main Concern / Signs and Symptoms *</Label>
                                    <Textarea id="main-concern" data-testid="main-concern-textarea" value={consultationInfo.main_concern} onChange={(e) => setConsultationInfo({ ...consultationInfo, main_concern: e.target.value })} placeholder="Describe the symptoms or concerns..." rows={5} />
                                </div>
                                <div>
                                    <Label htmlFor="symptom-duration">Kailan nagsimula ang sintomas? (Duration) *</Label>
                                    <Textarea id="symptom-duration" data-testid="symptom-duration-textarea" value={consultationInfo.symptom_duration} onChange={(e) => setConsultationInfo({ ...consultationInfo, symptom_duration: e.target.value })} placeholder="When did the symptoms start?" rows={3} />
                                </div>
                                <div>
                                    <Label htmlFor="appetite">Appetite *</Label>
                                    <Select value={consultationInfo.appetite} onValueChange={(value) => setConsultationInfo({ ...consultationInfo, appetite: value })}>
                                        <SelectTrigger id="appetite" data-testid="appetite-select">
                                            <SelectValue placeholder="Select appetite status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="normal">Normal</SelectItem>
                                            <SelectItem value="decreased">Decreased</SelectItem>
                                            <SelectItem value="none">None</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center space-x-2 p-4 border rounded-lg">
                                    <Checkbox 
                                        id="prescription" 
                                        data-testid="prescription-checkbox" 
                                        checked={consultationInfo.prescription_needed} 
                                        onCheckedChange={(checked) => setConsultationInfo({ ...consultationInfo, prescription_needed: checked })} 
                                    />
                                    <label htmlFor="prescription" className="text-sm font-medium cursor-pointer">
                                        I need a prescription for my pet
                                    </label>
                                </div>
                                <div>
                                    <Label>Upload Photos/Videos (Optional)</Label>
                                    <div {...getRootProps()} data-testid="dropzone" className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
                                        <input {...getInputProps()} />
                                        <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: '#d9a520' }} />
                                        <p className="text-sm text-muted-foreground">Drag & drop files here, or click to select</p>
                                        <p className="text-xs text-muted-foreground mt-2">Images or videos up to 10MB</p>
                                    </div>
                                    {uploadedFiles.length > 0 && (
                                        <p className="text-sm mt-2" style={{ color: '#d9a520' }}>
                                            {uploadedFiles.length} file(s) uploaded
                                        </p>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {/* Step 4: Schedule & Platform */}
                        {step === 4 && (
                            <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Preferred Schedule
                                </h2>
                                <div>
                                    <Label>Appointment Date *</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" data-testid="date-picker-trigger" className="w-full justify-start text-left font-normal">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {consultationInfo.preferred_schedule_date ? format(consultationInfo.preferred_schedule_date, 'PPP') : <span>Pick a date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar mode="single" selected={consultationInfo.preferred_schedule_date} onSelect={handleDateSelect} disabled={(date) => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return date < today;
                                            }} />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                {consultationInfo.preferred_schedule_date && (
                                    <div>
                                        <Label>Available Time Slots *</Label>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                                            {availableSlots.map((slot) => (
                                                <Button key={slot} data-testid={`time-slot-${slot}`} variant={consultationInfo.preferred_schedule_time === slot ? 'default' : 'outline'} onClick={() => setConsultationInfo({ ...consultationInfo, preferred_schedule_time: slot })} className="text-sm">
                                                    {slot}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="platform">Preferred Consultation Platform *</Label>
                                    <Select value={consultationInfo.preferred_platform} onValueChange={(value) => setConsultationInfo({ ...consultationInfo, preferred_platform: value })}>
                                        <SelectTrigger id="platform" data-testid="platform-select">
                                            <SelectValue placeholder="Select platform" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="viber">Viber</SelectItem>
                                            <SelectItem value="messenger">Messenger</SelectItem>
                                            <SelectItem value="google_meet">Google Meet</SelectItem>
                                            <SelectItem value="zoom">Zoom</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 5: Consent Form */}
                        {step === 5 && (
                            <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Online Vet Consultation Consent
                                </h2>
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                                        <Checkbox id="consent1" data-testid="consent-limitation" checked={consentForm.limitation_acknowledgment} onCheckedChange={(checked) => setConsentForm({ ...consentForm, limitation_acknowledgment: checked })} />
                                        <label htmlFor="consent1" className="text-sm leading-relaxed cursor-pointer">
                                            I understand that online vet consult has limitations and does not fully replace a physical examination. I acknowledge that treatment recommendations are based solely on the information, images, and videos I provide.
                                        </label>
                                    </div>
                                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                                        <Checkbox id="consent2" data-testid="consent-liability" checked={consentForm.liability_agreement} onCheckedChange={(checked) => setConsentForm({ ...consentForm, liability_agreement: checked })} />
                                        <label htmlFor="consent2" className="text-sm leading-relaxed cursor-pointer">
                                            I agree that MVC Vet Telecare and its veterinarians will not be held liable for any complications arising from incomplete or inaccurate information provided during the online consultation.
                                        </label>
                                    </div>
                                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                                        <Checkbox id="consent3" data-testid="consent-emergency" checked={consentForm.emergency_understanding} onCheckedChange={(checked) => setConsentForm({ ...consentForm, emergency_understanding: checked })} />
                                        <label htmlFor="consent3" className="text-sm leading-relaxed cursor-pointer">
                                            I understand that emergency cases may still require immediate physical examination at a veterinary clinic.
                                        </label>
                                    </div>
                                    <div className="flex items-start space-x-3 p-4 border rounded-lg">
                                        <Checkbox id="consent4" data-testid="consent-accuracy" checked={consentForm.information_accuracy} onCheckedChange={(checked) => setConsentForm({ ...consentForm, information_accuracy: checked })} />
                                        <label htmlFor="consent4" className="text-sm leading-relaxed cursor-pointer">
                                            By submitting this form, I confirm that all information provided is true and correct to the best of my knowledge. Yes, I agree to the terms and conditions.
                                        </label>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 6: Review */}
                        {step === 6 && (
                            <motion.div key="step6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                                    Review Your Booking
                                </h2>
                                <div className="space-y-4" data-testid="booking-review">
                                    <div className="bg-muted p-4 rounded-lg">
                                        <h3 className="font-semibold mb-2" style={{ color: '#701a25' }}>Client Information</h3>
                                        <p><strong>Name:</strong> {clientInfo.full_name}</p>
                                        <p><strong>Email:</strong> {clientInfo.email}</p>
                                        <p><strong>Mobile:</strong> {clientInfo.mobile_number}</p>
                                        <p><strong>Address:</strong> {clientInfo.complete_address}</p>
                                    </div>
                                    <div className="bg-muted p-4 rounded-lg">
                                        <h3 className="font-semibold mb-2" style={{ color: '#701a25' }}>Pet Information</h3>
                                        <p><strong>Name:</strong> {petInfo.name}</p>
                                        <p><strong>Species:</strong> {petInfo.species} - {petInfo.breed}</p>
                                        <p><strong>Age:</strong> {petInfo.age} years, {petInfo.gender}</p>
                                        <p><strong>Weight:</strong> {petInfo.weight} {petInfo.weight_unit}</p>
                                        {petInfo.birth_year && <p><strong>Birth Year:</strong> {petInfo.birth_year}</p>}
                                        <p><strong>Spayed/Neutered:</strong> {petInfo.spayed_neutered}</p>
                                    </div>
                                    <div className="bg-muted p-4 rounded-lg">
                                        <h3 className="font-semibold mb-2" style={{ color: '#701a25' }}>Consultation</h3>
                                        <p><strong>Type:</strong> {consultationInfo.consultation_type === 'general' ? 'General Consultation' : 'Emergency Consultation'}</p>
                                        <p><strong>Schedule:</strong> {format(consultationInfo.preferred_schedule_date, 'PPP')} at {consultationInfo.preferred_schedule_time}</p>
                                        <p><strong>Platform:</strong> {consultationInfo.preferred_platform}</p>
                                        <p><strong>Prescription Needed:</strong> {consultationInfo.prescription_needed ? 'Yes' : 'No'}</p>
                                        <p className="mt-2"><strong>Main Concern:</strong> {consultationInfo.main_concern}</p>
                                        <p><strong>Appetite:</strong> {consultationInfo.appetite}</p>
                                    </div>
                                    <div className="bg-secondary/10 p-4 rounded-lg">
                                        <p className="text-lg font-semibold" style={{ color: '#701a25' }}>Consultation Fee: ₱{consultationFee}</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8">
                        {step > 1 && (
                            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="prev-step-btn">
                                <ArrowLeft className="mr-2" /> Previous
                            </Button>
                        )}
                        {step < 6 ? (
                            <Button onClick={() => setStep(step + 1)} disabled={!validateStep()} data-testid="next-step-btn" className="btn-primary ml-auto">
                                Next <ArrowRight className="ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={handleSubmit} disabled={loading} data-testid="proceed-to-payment-btn" className="btn-secondary ml-auto">
                                {loading ? 'Processing...' : 'Proceed to Payment'}
                            </Button>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default BookingPage;
